import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { useOrganization } from "@/auth/organization";
import { useUser } from "@/auth/session";
import { Badge, cx, ErrorState, FullPageSpinner, Icon, LinkButton, type IconName } from "@/components/ui";
import type { Tone } from "@/components/ui/Badge";
import { portal } from "@/lib/api";
import { config } from "@/lib/config";
import {
  formatActivityTime,
  formatClock,
  formatDate,
  formatDayMonth,
  formatInZone,
  formatPrice,
  initials,
} from "@/lib/format";
import { primarySite, siteKeys, useOverview } from "@/lib/queries";
import type { CustomDomain, Deployment, DomainStatus, Overview, Site, User } from "@/lib/types";

/**
 * Home — Figma "Home" frame, MVP cut: the services the platform runs today (website, domain,
 * subscription) and what happened recently. Email, Analytics and Growth Intelligence cards join
 * when those services exist.
 */
export function DashboardPage() {
  const user = useUser();
  const { current } = useOrganization();
  const overview = useOverview();

  if (overview.isPending) return <FullPageSpinner />;
  if (overview.isError) return <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />;
  const site = primarySite(overview.data.recent_sites);
  const checkedAt = formatClock(overview.dataUpdatedAt);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold text-fg">
          {greeting()}
          {user.display_name ? `, ${user.display_name.split(" ")[0]}` : ""}
        </h1>
        <p className="text-sm text-fg-subtle">Here’s how {current.organization.name} is doing today.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <WebsiteCard site={site} deployments={overview.data.recent_deployments} checkedAt={checkedAt} />
        <DomainCard site={site} />
        <SubscriptionCard overview={overview.data} />
      </div>

      <RecentActivity overview={overview.data} user={user} />
    </div>
  );
}

function greeting(now = new Date()): string {
  // The hour in the display time zone, not the browser's: "morning" is the business's morning.
  const h = Number(formatInZone(now, { hour: "numeric", hourCycle: "h23" }, "en-US"));
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}

// ── Service cards ───────────────────────────────────────────────────────────

function ServiceCard({
  icon,
  title,
  status,
  children,
  action,
}: {
  icon: IconName;
  title: string;
  status: [Tone, string];
  children: ReactNode;
  action: ReactNode;
}) {
  return (
    <section className="flex min-h-[218px] min-w-0 flex-col gap-3 rounded-[18px] border border-border bg-surface px-5 py-[18px] shadow-[0px_2px_8px_0px_rgba(112,71,46,0.05)]">
      <div className="flex items-center gap-2.5">
        <span className="flex size-[30px] shrink-0 items-center justify-center rounded-[9px] border border-border bg-canvas text-fg-muted">
          <Icon name={icon} size={15} />
        </span>
        <h2 className="truncate text-[14.5px] font-semibold text-fg">{title}</h2>
        <span className="flex-1" />
        <Badge tone={status[0]}>{status[1]}</Badge>
      </div>
      {children}
      <span className="flex-1" />
      {action}
    </section>
  );
}

function Row({ label, value, strong }: { label: string; value: ReactNode; strong?: boolean }) {
  return (
    <div className="flex min-w-0 items-center gap-2 text-[12.5px]">
      <span className="shrink-0 text-fg-subtle">{label}</span>
      <span className="flex-1" />
      <span className={cx("truncate text-fg", strong && "font-semibold")}>{value}</span>
    </div>
  );
}

function Note({ children }: { children: ReactNode }) {
  return <p className="text-[11.5px] leading-[1.45] text-fg-subtle">{children}</p>;
}

const ACTION = "self-start text-[12.5px] font-semibold text-brand-500 hover:text-brand-600";

function CardLink({ to, children }: { to: string; children: ReactNode }) {
  return /^https?:/.test(to) ? (
    <a href={to} className={ACTION}>
      {children} →
    </a>
  ) : (
    <Link to={to} className={ACTION}>
      {children} →
    </Link>
  );
}

function WebsiteCard({
  site,
  deployments,
  checkedAt,
}: {
  site: Site | undefined;
  deployments: Deployment[];
  checkedAt: string;
}) {
  if (!site) {
    return (
      <ServiceCard
        icon="browser"
        title="Website"
        status={["neutral", "Not started"]}
        action={
          <LinkButton to="/websites" requires="websites:create" className={ACTION}>
            Create website →
          </LinkButton>
        }
      >
        <Note>
          Describe your business and the AI builder designs a website for it — ready to publish in minutes.
        </Note>
      </ServiceCard>
    );
  }
  const own = deployments.filter((d) => d.site_id === site.id).sort((a, b) => b.number - a.number);
  const live = own.find((d) => d.id === site.live_deployment_id);
  const latest = own[0];
  const status: [Tone, string] =
    site.status === "pending_deletion"
      ? ["warning", "Scheduled for deletion"]
      : site.status === "offline"
        ? ["danger", "Offline"]
        : site.live_deployment_id
          ? ["success", "Live"]
          : ["warning", "Not published"];

  return (
    <ServiceCard
      icon="browser"
      title="Website"
      status={status}
      action={
        <LinkButton
          to={`${config.builderUrl}/`}
          requires={{ permission: "websites:update", on: ["site", site.id] }}
          className={ACTION}
        >
          Edit website →
        </LinkButton>
      }
    >
      <Row
        label="Address"
        strong
        value={
          <a href={site.url} target="_blank" rel="noreferrer" className="hover:underline">
            {site.hostname}
          </a>
        }
      />
      <Row
        label="Published version"
        value={
          live
            ? `v${live.number} · ${formatDayMonth(live.finished_at ?? live.created_at)}`
            : site.live_deployment_id
              ? "Live"
              : "Not yet"
        }
      />
      <Row
        label="Latest build"
        value={
          !latest
            ? "None"
            : latest.is_live
              ? "Same as live"
              : `v${latest.number} · ${DEPLOYMENT_LABEL[latest.status]}`
        }
      />
      <Note>Source: website service · checked {checkedAt}</Note>
    </ServiceCard>
  );
}

const DEPLOYMENT_LABEL: Record<Deployment["status"], string> = {
  queued: "Queued",
  building: "Building",
  ready: "Ready",
  failed: "Failed",
  unknown: "Unknown",
};

const DOMAIN_STATUS: Record<DomainStatus, [Tone, string]> = {
  active: ["success", "Connected"],
  pending_verification: ["warning", "Needs attention"],
  failed: ["danger", "Failed"],
  unknown: ["neutral", "Unknown"],
};

function DomainCard({ site }: { site: Site | undefined }) {
  const { orgId } = useOrganization();
  const domains = useQuery({
    queryKey: siteKeys.domains(orgId, site?.id ?? ""),
    queryFn: () => portal.domains(site!.id),
    enabled: Boolean(site),
  });

  if (!site || (domains.data && domains.data.domains.length === 0)) {
    return (
      <ServiceCard
        icon="globe"
        title="Primary domain"
        status={["neutral", "Not connected"]}
        action={<CardLink to="/domains">Connect a domain</CardLink>}
      >
        {site && <Row label="Free address" strong value={site.hostname} />}
        <Note>Give your website its own address, like yourbusiness.com, so customers can find you.</Note>
      </ServiceCard>
    );
  }
  if (!domains.data) {
    return (
      <ServiceCard icon="globe" title="Primary domain" status={["neutral", "Checking"]} action={null}>
        <Note>{domains.isError ? "Couldn't load your domains." : "Loading…"}</Note>
      </ServiceCard>
    );
  }
  const domain = pickPrimary(domains.data.domains);
  return (
    <ServiceCard
      icon="globe"
      title="Primary domain"
      status={DOMAIN_STATUS[domain.status]}
      action={
        <CardLink to="/domains">{domain.status === "active" ? "Manage domains" : "Finish setup"}</CardLink>
      }
    >
      <Row label="Domain" strong value={domain.hostname} />
      <Row
        label="Status"
        value={
          domain.status === "active"
            ? `Verified ${formatDate(domain.verified_at)}`
            : domain.status === "pending_verification"
              ? "Waiting for DNS records"
              : (domain.check_error ?? "Verification failed")
        }
      />
      <Row label="Points to" value={site.hostname} />
      <Note>Source: domain service · checked {formatClock(domains.dataUpdatedAt)}</Note>
    </ServiceCard>
  );
}

function pickPrimary(domains: CustomDomain[]): CustomDomain {
  // Only called with at least one domain.
  return domains.find((d) => d.status === "active") ?? domains[0]!;
}

function SubscriptionCard({ overview }: { overview: Overview }) {
  const { plan } = overview.account;
  const { usage } = overview;
  return (
    <ServiceCard
      icon="credit-card"
      title="Subscription"
      status={["success", "Active"]}
      action={<CardLink to="/billing">Manage billing</CardLink>}
    >
      <Row label="Plan" strong value={plan.name} />
      <Row
        label="Price"
        value={
          !plan.monthly_price_cents
            ? "No charge"
            : overview.account.billing_period === "annual"
              ? `${formatPrice(plan.annual_price_cents, plan.currency)} / year`
              : `${formatPrice(plan.monthly_price_cents, plan.currency)} / month`
        }
      />
      <Row label="Custom domains" value={limit(usage.custom_domains, plan.max_custom_domains)} />
      {plan.max_custom_domains === 0 && <Note>Upgrade to connect your own domain.</Note>}
    </ServiceCard>
  );
}

function limit(used: number, max: number): string {
  return max === 0 ? "Not included" : `${used} of ${max} used`;
}

// ── Recent activity ─────────────────────────────────────────────────────────

interface Activity {
  key: string;
  at: string;
  text: string;
  avatar: string;
  mine: boolean;
}

function activities(overview: Overview, user: User): Activity[] {
  const siteName = new Map(overview.recent_sites.map((s) => [s.id, s.name]));
  const me = user.display_name || user.email;
  const items: Activity[] = overview.recent_sites
    .filter((s) => s.created_at)
    .map((s) => ({
      key: `site-${s.id}`,
      at: s.created_at!,
      text: `Website ${s.name} was created`,
      avatar: initials(s.name),
      mine: false,
    }));
  for (const d of overview.recent_deployments) {
    if (!d.created_at) continue;
    const site = siteName.get(d.site_id) ?? "your website";
    const mine = d.created_by_user_id === user.id;
    const who = mine ? me.split(" ")[0] : "A teammate";
    items.push({
      key: `deployment-${d.id}`,
      at: d.finished_at ?? d.created_at,
      text:
        d.status === "failed"
          ? `Version ${d.number} of ${site} failed to build`
          : d.status === "queued" || d.status === "building"
            ? `Version ${d.number} of ${site} is building`
            : d.is_live
              ? `${who} published ${site} version ${d.number}`
              : `${who} built ${site} version ${d.number}`,
      avatar: d.status === "failed" ? "SY" : mine ? initials(me) : "TM",
      mine,
    });
  }
  return items.sort((a, b) => b.at.localeCompare(a.at)).slice(0, 5);
}

function RecentActivity({ overview, user }: { overview: Overview; user: User }) {
  const items = activities(overview, user);
  return (
    <section className="flex flex-col gap-3 rounded-[18px] border border-border bg-surface px-[22px] py-5 shadow-[0px_2px_8px_0px_rgba(112,71,46,0.05)]">
      <div className="flex items-start gap-2">
        <h2 className="text-base font-bold text-fg">Recent activity</h2>
        <span className="flex-1" />
        <Link to="/websites" className="text-[12.5px] font-semibold text-brand-500 hover:text-brand-600">
          View all
        </Link>
      </div>
      {items.length === 0 ? (
        <p className="py-1.5 text-[13.5px] text-fg-subtle">
          Nothing yet — create a website and publish it from the builder to see activity here.
        </p>
      ) : (
        <ul className="flex flex-col">
          {items.map((a) => (
            <li key={a.key} className="flex items-center gap-3 py-1.5">
              <span
                className={cx(
                  "flex size-7 shrink-0 items-center justify-center rounded-full text-[10.5px] font-semibold text-fg-muted",
                  a.mine ? "bg-brand-100" : "bg-muted",
                )}
              >
                {a.avatar}
              </span>
              <p className="min-w-0 flex-1 truncate text-[13.5px] text-fg">{a.text}</p>
              <time dateTime={a.at} className="shrink-0 text-xs text-fg-subtle">
                {formatActivityTime(a.at)}
              </time>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
